// Landing page for the emailed confirmation link (/verify-email?token=…).
// Unauthenticated on purpose: the link is often opened in a different
// browser than the one that registered.
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle } from "../../components/icons";
import { api } from "../../api/api";
import { asApiError } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Spinner } from "../../components/ui";
import { AuthShell } from "./AuthShell";

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { user, refresh } = useAuth();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"working" | "ok" | "failed">("working");
  const [error, setError] = useState<string | null>(null);
  // The token is single-use — StrictMode's double effect must not burn it.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (!token) {
      setState("failed");
      return;
    }
    api
      .verifyEmail(token)
      .then(async () => {
        setState("ok");
        // Always re-read the session: `user` here is the value captured when
        // the effect ran — typically null while the session was still
        // loading — so gating on it skipped the refresh and left the
        // "confirm your email" banner up in a signed-in tab. refresh() on a
        // signed-out tab just resolves to no user.
        await refresh();
      })
      .catch((err) => {
        setState("failed");
        setError(asApiError(err).message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <AuthShell title={t("auth.verifyTitle", { defaultValue: "Email confirmation" })}>
      {state === "working" && (
        <div className="flex items-center gap-3 text-sm text-muted">
          <Spinner /> {t("auth.verifyWorking", { defaultValue: "Confirming your address…" })}
        </div>
      )}
      {state === "ok" && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-good">
            <CheckCircle2 size={18} />
            <span className="font-medium">
              {t("auth.verifyOk", { defaultValue: "Your email address is confirmed." })}
            </span>
          </div>
          <Link to={user ? "/app" : "/login"} className="btn-primary w-full justify-center">
            {user
              ? t("nav.sites", { defaultValue: "My sites" })
              : t("auth.signIn")}
          </Link>
        </div>
      )}
      {state === "failed" && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 text-bad">
            <XCircle size={18} className="mt-0.5 shrink-0" />
            <span className="text-sm">
              {error ??
                t("auth.verifyFailed", {
                  defaultValue: "This confirmation link is invalid or has expired.",
                })}
            </span>
          </div>
          <p className="text-sm text-muted">
            {t("auth.verifyRetry", {
              defaultValue: "Sign in and request a new confirmation email from your account page.",
            })}
          </p>
          <Link to={user ? "/app/account" : "/login"} className="btn-primary w-full justify-center">
            {user ? t("nav.account") : t("auth.signIn")}
          </Link>
        </div>
      )}
    </AuthShell>
  );
}
